<script lang="ts">
  import { app, persistSettings } from './store.svelte';
  import { t, type Lang } from './i18n';
  import type { AppMode } from './store.svelte';
  import Icon from './lib/Icon.svelte';
  import IconButton from './lib/IconButton.svelte';
  import ModeTab from './lib/ModeTab.svelte';

  interface Props {
    onSetMode: (m: AppMode) => void;
    onSaveProject?: () => void;
    onLoadProject?: () => void;
    onExportMesh?: (format: 'stl' | 'stl-ascii' | 'obj' | '3mf') => void;
  }
  const { onSetMode, onSaveProject, onLoadProject, onExportMesh }: Props = $props();
  let exportMenuOpen = $state(false);
  function toggleExport() { exportMenuOpen = !exportMenuOpen; }
  function pickExport(fmt: 'stl' | 'stl-ascii' | 'obj' | '3mf') {
    exportMenuOpen = false;
    onExportMesh?.(fmt);
  }

  const modes: { id: AppMode; key: 'mode.edit' | 'mode.sculpt' | 'mode.slice' | 'mode.preview' | 'mode.studio'; label?: string; icon: string }[] = [
    { id: 'studio',  key: 'mode.studio',  label: 'Stüdyo',     icon: 'sparkles' },
    { id: 'param',   key: 'mode.studio',  label: 'Parametrik', icon: 'workflow' },
    { id: 'edit',    key: 'mode.edit',    icon: 'edit-3' },
    { id: 'sculpt',  key: 'mode.sculpt',  icon: 'brush' },
    { id: 'slice',   key: 'mode.slice',   icon: 'layers' },
    { id: 'preview', key: 'mode.preview', icon: 'eye' }
  ];

  function setLang(lang: Lang) { app.lang = lang; persistSettings(); }
  function toggleTheme() {
    app.theme = app.theme === 'dark' ? 'light' : 'dark';
    persistSettings();
    document.documentElement.dataset.theme = app.theme;
  }
</script>

<header>
  <div class="brand">
    <div class="logo">
      <svg class="logo-mark" viewBox="0 0 32 32" aria-hidden="true">
        <!-- Vase silhouette: narrow neck flares to belly, tapers to base.
             Gradient fill suggests wet clay turning on a wheel. -->
        <defs>
          <linearGradient id="cf-clay" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stop-color="#fbcf8a"/>
            <stop offset="55%" stop-color="#f59e4b"/>
            <stop offset="100%" stop-color="#b85f1f"/>
          </linearGradient>
          <linearGradient id="cf-rim" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#ffe1b8"/>
            <stop offset="100%" stop-color="#f59e4b" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <!-- Vase body using a smooth Bezier silhouette mirrored into a closed shape -->
        <path d="M11 5
                 C 11 5, 10 7, 11 8.5
                 C 12 10, 13 11, 12 13
                 C 8 17, 7 22, 9 26
                 C 10 28, 13 29, 16 29
                 C 19 29, 22 28, 23 26
                 C 25 22, 24 17, 20 13
                 C 19 11, 20 10, 21 8.5
                 C 22 7, 21 5, 21 5 Z"
              fill="url(#cf-clay)"
              stroke="rgba(255,255,255,0.18)" stroke-width="0.6"/>
        <!-- Top rim highlight -->
        <ellipse cx="16" cy="5.4" rx="5.2" ry="1.1" fill="url(#cf-rim)"/>
        <!-- Belly shimmer -->
        <path d="M9.5 19 C 11 17, 13 16, 15 16.5"
              stroke="rgba(255,255,255,0.35)" stroke-width="0.8" fill="none" stroke-linecap="round"/>
      </svg>
      <span class="logo-glow" aria-hidden="true"></span>
    </div>
    <div class="title">
      <span class="appname">ClayForge</span>
      <span class="ver">v1.2</span>
    </div>
  </div>

  <nav class="tabs-mode" aria-label="Mode">
    {#each modes as m (m.id)}
      {@const hasContent = app.triangles.length > 0 || app.showVasePanel}
      {@const enabled = m.id === 'edit' || m.id === 'studio' || m.id === 'param' || hasContent}
      <ModeTab
        active={app.mode === m.id}
        disabled={!enabled}
        onclick={() => onSetMode(m.id)}
      >
        <Icon name={m.icon} size={15} />
        <span>{m.label ?? t(app.lang, m.key as 'mode.edit' | 'mode.sculpt' | 'mode.slice' | 'mode.preview')}</span>
      </ModeTab>
    {/each}
  </nav>

  <div class="header-controls">
    <span class="filename" title={app.fileName || 'Untitled'}>
      {app.fileName || 'Untitled'}{app.triangles.length > 0 && !app.gcodeOutput ? ' •' : ''}
    </span>
    <div class="chip-row">
      {#if onExportMesh}
        <div class="export-wrap">
          <IconButton
            onclick={toggleExport}
            disabled={app.triangles.length === 0 && !app.showVasePanel}
            active={exportMenuOpen}
            title="Modeli dışa aktar"
            size={34}
          >
            <Icon name="download" size={17} />
          </IconButton>
          {#if exportMenuOpen}
            <div class="export-menu" role="menu">
              <button role="menuitem" onclick={() => pickExport('stl')}>STL <em>(binary)</em></button>
              <button role="menuitem" onclick={() => pickExport('stl-ascii')}>STL <em>(ASCII)</em></button>
              <button role="menuitem" onclick={() => pickExport('obj')}>OBJ <em>(Wavefront)</em></button>
              <button role="menuitem" onclick={() => pickExport('3mf')}>3MF <em>(printer-ready)</em></button>
            </div>
          {/if}
        </div>
      {/if}
      {#if onLoadProject}
        <IconButton onclick={onLoadProject} title={t(app.lang, 'btn.load')} size={34}>
          <Icon name="folder-open" size={17} />
        </IconButton>
      {/if}
      {#if onSaveProject}
        <IconButton
          onclick={onSaveProject}
          title={t(app.lang, 'btn.save')}
          disabled={app.triangles.length === 0 && !app.showVasePanel}
          size={34}
        >
          <Icon name="save" size={17} />
        </IconButton>
      {/if}
      <button class="lang-pill" onclick={() => setLang(app.lang === 'tr' ? 'en' : 'tr')}>
        {app.lang.toUpperCase()}
      </button>
      <IconButton onclick={toggleTheme} title={t(app.lang, 'theme.label')} size={34}>
        <Icon name={app.theme === 'dark' ? 'sun' : 'moon'} size={17} />
      </IconButton>
    </div>
  </div>
</header>

<style>
  header {
    background:
      radial-gradient(140% 200% at 100% 0%, rgba(46, 150, 255, 0.07), transparent 55%),
      radial-gradient(80% 200% at 0% 0%, rgba(245, 158, 75, 0.05), transparent 55%),
      linear-gradient(180deg, var(--bg-header-from) 0%, var(--bg-header-to) 100%);
    padding: 12px 22px;
    gap: 22px;
    border-bottom: 1px solid var(--border-soft);
    display: flex;
    align-items: center;
    flex-shrink: 0;
    position: relative;
  }
  header::after {
    content: '';
    position: absolute; inset: auto 0 -1px 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(46, 150, 255, 0.4) 35%, rgba(245, 158, 75, 0.3) 65%, transparent);
    opacity: 0.55;
  }

  .brand { display: flex; align-items: center; gap: 12px; }
  .logo {
    position: relative;
    width: 38px; height: 38px;
    border-radius: 11px;
    display: grid; place-items: center;
    background:
      radial-gradient(120% 100% at 50% 0%, rgba(245, 158, 75, 0.25), transparent 70%),
      linear-gradient(135deg, #14233a 0%, #0a1626 100%);
    border: 1px solid rgba(245, 158, 75, 0.32);
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.05) inset,
      0 6px 22px -6px rgba(245, 158, 75, 0.45);
    overflow: hidden;
  }
  .logo-mark {
    position: relative; z-index: 1;
    width: 26px; height: 26px;
    filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));
    animation: logo-breath 5s ease-in-out infinite;
  }
  @keyframes logo-breath {
    50% { transform: scale(1.04); filter: drop-shadow(0 1px 4px rgba(245,158,75,0.55)); }
  }
  .logo-glow {
    position: absolute; inset: -50%;
    background: conic-gradient(from 0deg, transparent 75%, rgba(245,158,75,0.22), transparent);
    animation: spin 8s linear infinite;
    opacity: 0.55;
    pointer-events: none;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .title { display: flex; align-items: baseline; gap: 8px; }
  .appname {
    font-size: 17px; font-weight: 800; letter-spacing: -0.015em;
    background: linear-gradient(95deg, #f0f7ff 0%, #f59e4b 60%, #fbcf8a 100%);
    -webkit-background-clip: text; background-clip: text; color: transparent;
  }
  .ver {
    font-size: 10.5px; color: var(--text-muted); font-weight: 600;
    padding: 2px 7px;
    background: rgba(245, 158, 75, 0.08);
    border: 1px solid rgba(245, 158, 75, 0.18);
    border-radius: 5px;
    letter-spacing: 0.02em;
  }

  .tabs-mode {
    display: flex; gap: 2px; align-items: center; margin-left: 8px;
    padding: 4px;
    background: rgba(255,255,255,0.025);
    border: 1px solid rgba(255,255,255,0.04);
    border-radius: 12px;
  }

  .header-controls { margin-left: auto; display: flex; align-items: center; gap: 14px; }
  .filename {
    color: var(--text-3); font-size: 12.5px; font-variant-numeric: tabular-nums;
    max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    padding: 4px 10px;
    background: var(--chip-bg);
    border-radius: 6px;
    border: 1px solid var(--border-soft);
  }
  .chip-row { display: flex; gap: 6px; align-items: center; }

  .lang-pill {
    background: var(--chip-bg);
    color: var(--text-2);
    border: 1px solid var(--border-soft);
    padding: 7px 11px;
    font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
    border-radius: 8px;
    cursor: pointer; font-family: inherit;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .lang-pill:hover { background: var(--chip-bg-hover); color: var(--text-bright); border-color: rgba(255,255,255,0.12); }

  .export-wrap { position: relative; }
  .export-menu {
    position: absolute; top: calc(100% + 6px); right: 0;
    background: var(--bg-2);
    border: 1px solid var(--border-soft);
    border-radius: 10px; padding: 5px;
    min-width: 180px; z-index: 50;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.04) inset,
      0 16px 40px -10px rgba(0,0,0,0.55);
    display: flex; flex-direction: column; gap: 2px;
    animation: menu-in 0.15s ease-out;
  }
  @keyframes menu-in {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .export-menu button {
    background: transparent; border: none; color: var(--text-2);
    padding: 8px 12px; border-radius: 7px; text-align: left;
    font-size: 12px; font-family: inherit; cursor: pointer;
    display: flex; align-items: baseline; gap: 6px;
    transition: background 0.12s, color 0.12s;
  }
  .export-menu button em { color: var(--text-muted); font-style: normal; font-size: 10.5px; }
  .export-menu button:hover { background: var(--chip-bg-hover); color: var(--text-bright); }

  @media (max-width: 900px) {
    .filename { display: none; }
    .ver { display: none; }
  }
</style>
